import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Image as ImageIcon, Trash2, Download, Plus, X,
  Loader2, FlaskConical, TrendingUp, CalendarDays,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import AppLayout from "../components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CATEGORIES = ["Blood test", "Scan / imaging", "Clinic letter", "Prescription", "Other result"];
const COMMON_MARKERS = ["CRP", "ESR", "ANA", "Anti-CCP", "Rheumatoid Factor", "TSH", "Free T4", "Vitamin D", "Ferritin", "HbA1c", "Anti-TPO", "Complement C3", "Complement C4"];

export default function MyRecords() {
  const [records, setRecords] = useState([]);
  const [markers, setMarkers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([axios.get(`${API}/records`), axios.get(`${API}/markers`)]);
      setRecords(r.data.records || []);
      setMarkers(m.data.markers || {});
    } catch { toast.error("Could not load your records"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const download = async (rec) => {
    try {
      const res = await axios.get(`${API}/records/${rec.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const remove = async (rec) => {
    if (!window.confirm(`Delete "${rec.title}"?`)) return;
    try { await axios.delete(`${API}/records/${rec.id}`); toast.success("Record deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const markerNames = Object.keys(markers);

  return (
    <AppLayout title="My Records">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><FlaskConical size={26} /></div>
          <div>
            <h2 className="font-head font-bold text-xl">Your health records</h2>
            <p className="text-white/85 text-sm mt-0.5">Securely store blood tests, scans and letters — and trend key markers over time.</p>
          </div>
        </div>
        <button data-testid="add-record-btn" onClick={() => setShowForm(true)} className="hidden sm:inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-white text-[#0f766e] font-semibold text-sm hover:bg-white/90 transition-colors"><Plus size={18} /> Add record</button>
      </div>
      <button onClick={() => setShowForm(true)} className="sm:hidden w-full mt-4 inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-[#0d9488] text-white font-semibold text-sm"><Plus size={18} /> Add record</button>

      {markerNames.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-5" data-testid="marker-trends">
          <h3 className="font-head font-semibold text-slate-800 flex items-center gap-2"><TrendingUp size={18} className="text-[#0d9488]" /> Blood marker trends</h3>
          <p className="text-[12.5px] text-slate-400 mb-2">From values you tagged on your uploads.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-2">
            {markerNames.map((name) => (
              <div key={name}>
                <p className="text-[13px] font-semibold text-slate-600 mb-1">{name} <span className="text-slate-400 font-normal">({markers[name][markers[name].length - 1]?.unit})</span></p>
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={markers[name]} margin={{ left: -18, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#0d9488" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="font-head font-semibold text-slate-800 mt-6 mb-3">Stored records</h3>
      {loading ? (
        <div className="p-12 text-center"><Loader2 className="animate-spin text-[#0d9488] mx-auto" /></div>
      ) : records.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="records-empty">
          <UploadCloud size={40} className="text-slate-300 mx-auto" />
          <p className="text-slate-500 mt-3">No records yet. Upload your first blood test or scan result.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="records-list">
          {records.map((rec) => {
            const isImg = (rec.content_type || "").startsWith("image/");
            return (
              <div key={rec.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center shrink-0">{isImg ? <ImageIcon size={20} /> : <FileText size={20} />}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold text-slate-800 truncate">{rec.title}</h4>
                    <button data-testid={`delete-record-${rec.id}`} onClick={() => remove(rec)} className="text-slate-300 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[11px] font-semibold text-[#0f766e] bg-[#e6f4f2] px-2 py-0.5 rounded-full">{rec.category}</span>
                    <span className="text-[12px] text-slate-400 inline-flex items-center gap-1"><CalendarDays size={12} /> {rec.date}</span>
                  </div>
                  {rec.notes && <p className="text-[13px] text-slate-500 mt-1.5 line-clamp-2">{rec.notes}</p>}
                  {rec.markers?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {rec.markers.map((m, i) => <span key={`${m.name}-${i}`} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{m.name}: {m.value}{m.unit}</span>)}
                    </div>
                  )}
                  <button onClick={() => download(rec)} className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-[#0d9488] hover:text-[#0f766e]"><Download size={14} /> View / download</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[12px] text-slate-400 mt-6">Your files are stored securely and privately, visible only to you. This is a personal record store, not a medical diagnosis.</p>

      {showForm && <UploadForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} uploading={uploading} setUploading={setUploading} />}
    </AppLayout>
  );
}

function UploadForm({ onClose, onDone, uploading, setUploading }) {
  const fileRef = useRef();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Blood test");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [markerRows, setMarkerRows] = useState([]);

  const addRow = () => setMarkerRows((r) => [...r, { _k: `${Date.now()}-${Math.random().toString(36).slice(2)}`, name: "", value: "", unit: "", date }]);
  const setRow = (i, k, v) => setMarkerRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const rmRow = (i) => setMarkerRows((r) => r.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file");
    if (!title.trim()) return toast.error("Please add a title");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("category", category);
    fd.append("date", date);
    fd.append("notes", notes);
    fd.append("markers", JSON.stringify(markerRows.filter((m) => m.name && m.value)));
    setUploading(true);
    try {
      await axios.post(`${API}/records`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Record uploaded");
      onDone();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const field = "w-full h-11 px-3 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488]";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="upload-form" className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 max-h-[92vh] overflow-y-auto kl-scroll">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head font-bold text-lg text-slate-800">Add a health record</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <label data-testid="file-drop" onClick={() => fileRef.current?.click()} className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 rounded-xl py-6 cursor-pointer hover:border-[#0d9488]">
            <UploadCloud size={26} className="text-[#0d9488]" />
            <span className="text-sm text-slate-600">{file ? file.name : "Tap to choose a PDF or image"}</span>
            <span className="text-[11px] text-slate-400">PDF, JPG, PNG · max 15MB</span>
            <input ref={fileRef} data-testid="file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { setFile(e.target.files[0]); if (!title && e.target.files[0]) setTitle(e.target.files[0].name.replace(/\.[^.]+$/, "")); }} />
          </label>
          <input data-testid="record-title" className={field} placeholder="Title (e.g. Full Blood Count)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="record-category" className={field} value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <input data-testid="record-date" type="date" className={field} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <textarea className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#0d9488]" rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />

          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-semibold text-slate-600">Tag key results (optional) — trend them over time</p>
              <button type="button" onClick={addRow} className="text-[13px] font-semibold text-[#0d9488] inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </div>
            {markerRows.map((row, i) => (
              <div key={row._k} className="flex gap-2 mt-2">
                <input list="marker-names" className="flex-1 h-10 px-2 rounded-lg border border-slate-200 text-[13px]" placeholder="Marker" value={row.name} onChange={(e) => setRow(i, "name", e.target.value)} />
                <input className="w-20 h-10 px-2 rounded-lg border border-slate-200 text-[13px]" placeholder="Value" value={row.value} onChange={(e) => setRow(i, "value", e.target.value)} />
                <input className="w-16 h-10 px-2 rounded-lg border border-slate-200 text-[13px]" placeholder="Unit" value={row.unit} onChange={(e) => setRow(i, "unit", e.target.value)} />
                <button type="button" onClick={() => rmRow(i)} className="text-slate-300 hover:text-red-500"><X size={16} /></button>
              </div>
            ))}
            <datalist id="marker-names">{COMMON_MARKERS.map((m) => <option key={m} value={m} />)}</datalist>
          </div>
        </div>
        <button data-testid="upload-submit" disabled={uploading} className="w-full mt-5 h-11 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-50 text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
          {uploading && <Loader2 size={16} className="animate-spin" />} Upload record
        </button>
      </form>
    </div>
  );
}
